import { ICommand } from 'react-ace';
import { Ace } from 'ace-builds';
import { useMemo } from 'react';
import * as R from 'remeda';
import {
  SchemaItem,
  SchemaSection,
  SchemaTuple,
} from '@rossum/api-client/schemas';
import { customCommands } from '../constants';
import annotationCompleters from './annotationCompletions.json';
import annotationDocumentCompleters from './annotationDocumentCompletions.json';
import annotationEmailCompletions from './annotationEmailCompletions.json';
import {
  contextCompleterRules,
  contextTopLevelCompletions,
} from './contextSideloads';
import {
  getAceCompleter,
  getFieldCompletions,
  matchAnnotationDocumentRefPrefix,
  matchAnnotationEmailRefPrefix,
  matchAnnotationRefPrefix,
  matchContextRefPrefix,
  matchMultivalueDatapointReference,
  matchSchemaFieldRefPrefix,
} from './helpers';

const allValuesCompletions: Ace.Completion[] = [
  {
    caption: 'all_values',
    value: 'all_values',
    meta: 'attribute',
  },
];

export const useCommands = (
  fields: Array<SchemaSection | SchemaItem>,
  defaultCompleters: Array<Ace.Completer>,
  currentFormulaId: string | undefined
): ICommand[] => {
  const commands: ICommand[] = useMemo(() => {
    const fieldCompletionsWithoutPrefix = getFieldCompletions(fields);

    const datapointIdsInTuples = R.fromEntries(
      fields
        .filter((field): field is SchemaTuple => field.category === 'tuple')
        .map(tuple => [tuple.id, tuple.children.map(child => child.id)])
    );

    const getCompletionsForToken = (
      lastToken: string
    ): Ace.Completion[] | null => {
      if (matchSchemaFieldRefPrefix(lastToken)) {
        return fieldCompletionsWithoutPrefix;
      }
      if (matchAnnotationRefPrefix(lastToken)) {
        return annotationCompleters;
      }
      if (matchAnnotationDocumentRefPrefix(lastToken)) {
        return annotationDocumentCompleters;
      }
      if (matchAnnotationEmailRefPrefix(lastToken)) {
        return annotationEmailCompletions;
      }
      if (matchContextRefPrefix(lastToken)) {
        return contextTopLevelCompletions;
      }
      const contextRule = contextCompleterRules.find(rule =>
        rule.test(lastToken)
      );
      if (contextRule) {
        return contextRule.completions;
      }
      if (
        matchMultivalueDatapointReference({
          currentFormulaId,
          datapointIdsInTuples,
          lastToken,
        })
      ) {
        return allValuesCompletions;
      }
      return null;
    };

    const handleDotCommand: ICommand['exec'] = editor => {
      const pos = editor.selection.getCursor();
      const curLine = editor.session.getDocument().getLine(pos.row);
      const curTokens = curLine.slice(0, pos.column).trim().split(/\s+/);

      const curCmd = curTokens[0];
      if (!curCmd) return;

      const lastToken = curTokens[curTokens.length - 1];
      if (!lastToken) return;

      editor.insert('.');

      const completions = getCompletionsForToken(lastToken);
      if (completions) {
        editor.completer?.detach?.();
        // oxlint-disable-next-line no-param-reassign -- Ace API requires mutating editor.completers
        editor.completers = [
          getAceCompleter(completions, () => {
            // oxlint-disable-next-line no-param-reassign -- Ace API requires mutating editor.completers
            editor.completers = defaultCompleters;
          }),
        ];
        editor.execCommand('startAutocomplete');
      }
    };

    return [
      {
        name: customCommands.fieldsDotCommand,
        bindKey: { win: '.', mac: '.' },
        exec: handleDotCommand,
      },
    ];
  }, [fields, defaultCompleters, currentFormulaId]);

  return commands;
};
